/** @format */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    TextField,
    Typography,
    CircularProgress,
    MenuItem,
    Alert,
    InputAdornment,
} from "@mui/material";
import {
    Business,
    ArrowBack,
    ArrowForward,
    Public,
    Lock,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    createBusiness,
    fetchMyBusinesses,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectBusinessMemberships,
    selectIsCreatingBusiness,
    selectCreateBusinessError,
    selectIsLoadingBusinesses,
} from "../../redux/selectors/collectSelector";
import { colors } from "../../utility/types/constant";
import {
    CollectPageWrapper,
    CollectHeroBanner,
    CollectHeroInner,
    CollectBadge,
    CollectHeroTitle,
    CollectHeroSubtitle,
    CollectSectionCard,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectInfoBanner,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// Collect Setup — first-run workspace creation.
// Reached from the Welcome screen on /collect when the user has no
// business memberships yet, or via "+ New workspace" later on.
// ─────────────────────────────────────────────────────────────────────────

const COMMON_TIMEZONES = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Phoenix",
    "America/Anchorage",
    "America/Toronto",
    "America/Vancouver",
    "America/Mexico_City",
    "Europe/London",
    "Europe/Dublin",
    "Europe/Paris",
    "Europe/Berlin",
    "Europe/Madrid",
    "Africa/Lagos",
    "Africa/Johannesburg",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Australia/Sydney",
    "UTC",
];

function detectBrowserTimezone(): string {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return tz || "UTC";
    } catch {
        return "UTC";
    }
}

// ── Pill-style step indicator ────────────────────────────────────────────
const SetupSteps: React.FC<{ activeIndex: number }> = ({ activeIndex }) => {
    const steps = ["Create workspace", "Connect QuickBooks", "Automate reminders"];
    return (
        <Box
            sx={{
                display: "flex",
                gap: { xs: 1, sm: 1.5 },
                justifyContent: "center",
                flexWrap: "wrap",
                mb: 4,
            }}
        >
            {steps.map((label, idx) => {
                const isActive = idx === activeIndex;
                const isDone = idx < activeIndex;
                return (
                    <Box
                        key={label}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: { xs: 1.25, sm: 2 },
                            py: 1,
                            borderRadius: "999px",
                            backgroundColor: isActive
                                ? `${colors.mainSecondary}1A`
                                : "rgba(15,23,42,0.04)",
                            border: `1px solid ${isActive
                                    ? `${colors.mainSecondary}55`
                                    : "rgba(15,23,42,0.06)"
                                }`,
                        }}
                    >
                        <Box
                            sx={{
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                backgroundColor:
                                    isActive || isDone
                                        ? colors.mainSecondary
                                        : "rgba(15,23,42,0.1)",
                                color: isActive || isDone ? "#fff" : "#6b7280",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.72rem",
                                fontWeight: 800,
                                fontFamily: "'Montserrat', sans-serif",
                            }}
                        >
                            {isDone ? "✓" : idx + 1}
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: { xs: "0.72rem", sm: "0.8rem" },
                                fontWeight: isActive ? 700 : 600,
                                color: isActive ? colors.primary : "#6b7280",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

const CollectSetup: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const memberships = useSelector(selectBusinessMemberships);
    const activeBusinessId = useSelector(selectActiveBusinessId);
    const isLoadingBusinesses = useSelector(selectIsLoadingBusinesses);
    const isCreating = useSelector(selectIsCreatingBusiness);
    const createError = useSelector(selectCreateBusinessError);

    const [name, setName] = useState("");
    const [timezone, setTimezone] = useState<string>(detectBrowserTimezone());
    const [submitted, setSubmitted] = useState(false);

    // Make sure we know whether the user already has businesses.
    useEffect(() => {
        if (memberships.length === 0 && !isLoadingBusinesses) {
            dispatch(fetchMyBusinesses());
        }
    }, [dispatch, memberships.length, isLoadingBusinesses]);

    // On success the slice updates activeBusinessId — bounce to dashboard.
    useEffect(() => {
        if (submitted && activeBusinessId && !isCreating && !createError) {
            navigate("/collect/dashboard", { replace: true });
        }
    }, [submitted, activeBusinessId, isCreating, createError, navigate]);

    const includeTz =
        timezone && !COMMON_TIMEZONES.includes(timezone)
            ? [timezone, ...COMMON_TIMEZONES]
            : COMMON_TIMEZONES;

    const canSubmit = name.trim().length >= 2 && !isCreating;

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitted(true);
        await dispatch(
            createBusiness({ name: name.trim(), timezone: timezone || undefined }),
        );
    };

    const hasExistingBusinesses = memberships.length > 0;

    // Shared MUI sx for the two form fields — keeps them visually consistent.
    const fieldSx = {
        "& .MuiOutlinedInput-root": {
            fontFamily: "'Montserrat', sans-serif",
            borderRadius: "12px",
            backgroundColor: "#fff",
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.mainSecondary,
                borderWidth: "1.5px",
            },
        },
        "& .MuiInputLabel-root": {
            fontFamily: "'Montserrat', sans-serif",
            color: "#6b7280",
            "&.Mui-focused": { color: colors.primary },
        },
        "& .MuiFormHelperText-root": {
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "0.74rem",
            marginLeft: "4px",
        },
    };

    return (
        <CollectPageWrapper>
            {/* ─── Compact, centered hero ─────────────────────────────── */}
            <CollectHeroBanner
                sx={{
                    padding: {
                        xs: "28px 16px 32px",
                        sm: "40px 20px 44px",
                        md: "48px 24px 56px",
                    },
                }}
            >
                <CollectHeroInner sx={{ alignItems: "center", textAlign: "center" }}>
                    <CollectBadge label="AARRP · Setup" sx={{ alignSelf: "center" }} />
                    <CollectHeroTitle sx={{ maxWidth: "720px" }}>
                        Create your Collect workspace
                    </CollectHeroTitle>
                    <CollectHeroSubtitle sx={{ mx: "auto" }}>
                        A workspace holds your QuickBooks connection, customers, invoices,
                        and automated reminder campaigns. You can rename it any time.
                    </CollectHeroSubtitle>
                </CollectHeroInner>
            </CollectHeroBanner>

            {/* ─── Body — centered single column, with proper top spacing ─ */}
            <Box
                sx={{
                    maxWidth: "720px",
                    margin: "0 auto",
                    padding: {
                        xs: "28px 16px 48px",
                        sm: "40px 20px 56px",
                        md: "56px 24px 72px",
                    },
                }}
            >
                <SetupSteps activeIndex={0} />

                {hasExistingBusinesses && (
                    <CollectInfoBanner tone="info">
                        <Box flex={1}>
                            You already have {memberships.length} workspace
                            {memberships.length === 1 ? "" : "s"}. You can create another, or
                            head back.
                        </Box>
                        <CollectGhostButton
                            startIcon={<ArrowBack fontSize="small" />}
                            onClick={() => navigate("/collect/dashboard")}
                        >
                            Dashboard
                        </CollectGhostButton>
                    </CollectInfoBanner>
                )}

                {/* ─── Main form card ─────────────────────────────────── */}
                <CollectSectionCard
                    sx={{
                        padding: { xs: "20px 16px", sm: "28px", md: "36px 40px" },
                        marginBottom: 3,
                    }}
                >
                    <Box mb={3}>
                        <CollectSectionTitle>Workspace details</CollectSectionTitle>
                        <CollectSectionSubtitle>
                            A friendly name and a timezone are all we need.
                        </CollectSectionSubtitle>
                    </Box>

                    <form onSubmit={onSubmit} noValidate>
                        <Grid container spacing={2.5}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Business name"
                                    placeholder="e.g. Acme Studios LLC"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    fullWidth
                                    required
                                    autoFocus
                                    inputProps={{ maxLength: 120 }}
                                    helperText="The name your customers see in reminder emails."
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Business
                                                    fontSize="small"
                                                    sx={{ color: "#9ca3af" }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    select
                                    label="Timezone"
                                    value={timezone}
                                    onChange={(e) => setTimezone(e.target.value)}
                                    fullWidth
                                    helperText="Used for scheduling reminder send windows."
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Public
                                                    fontSize="small"
                                                    sx={{ color: "#9ca3af" }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx}
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: {
                                                sx: { maxHeight: 320, borderRadius: "12px" },
                                            },
                                        },
                                    }}
                                >
                                    {includeTz.map((tz) => (
                                        <MenuItem
                                            key={tz}
                                            value={tz}
                                            sx={{ fontFamily: "'Montserrat', sans-serif" }}
                                        >
                                            {tz}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {createError && (
                                <Grid item xs={12}>
                                    <Alert
                                        severity="error"
                                        sx={{
                                            borderRadius: "12px",
                                            fontFamily: "'Montserrat', sans-serif",
                                        }}
                                    >
                                        {createError}
                                    </Alert>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column-reverse", sm: "row" },
                                        justifyContent: "space-between",
                                        alignItems: { xs: "stretch", sm: "center" },
                                        gap: 1.5,
                                        pt: 1.5,
                                        borderTop: "1px solid rgba(15,23,42,0.06)",
                                    }}
                                >
                                    <CollectGhostButton
                                        onClick={() => navigate("/collect")}
                                        startIcon={<ArrowBack fontSize="small" />}
                                        sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
                                    >
                                        Cancel
                                    </CollectGhostButton>
                                    <CollectPrimaryButton
                                        type="submit"
                                        disabled={!canSubmit}
                                        endIcon={
                                            isCreating ? undefined : (
                                                <ArrowForward fontSize="small" />
                                            )
                                        }
                                        startIcon={
                                            isCreating ? (
                                                <CircularProgress
                                                    size={16}
                                                    sx={{ color: "#fff" }}
                                                />
                                            ) : undefined
                                        }
                                        sx={{
                                            width: { xs: "100%", sm: "auto" },
                                            minWidth: { sm: "210px" },
                                        }}
                                    >
                                        {isCreating ? "Creating workspace…" : "Create & continue"}
                                    </CollectPrimaryButton>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CollectSectionCard>

                {/* ─── Trust strip ────────────────────────────────────── */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "center",
                        mb: 3,
                    }}
                >
                    <Lock sx={{ fontSize: 16, color: colors.mainSecondary }} />
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.78rem",
                            color: "#6b7280",
                        }}
                    >
                        Your data stays private. You control who joins.
                    </Typography>
                </Box>

                {/* ─── What's next mini cards ─────────────────────────── */}
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            color: "#6b7280",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            textAlign: "center",
                            mb: 2,
                        }}
                    >
                        What happens after you create
                    </Typography>
                    <Grid container spacing={2}>
                        {[
                            {
                                title: "Connect QuickBooks",
                                body: "Securely link your QBO company. Read-only by default.",
                            },
                            {
                                title: "Sync your AR",
                                body: "Customers, invoices, and balances import in seconds.",
                            },
                            {
                                title: "Turn on reminders",
                                body: "A default campaign is pre-built — review and go live.",
                            },
                        ].map((step, idx) => (
                            <Grid item xs={12} sm={4} key={step.title}>
                                <Box
                                    sx={{
                                        backgroundColor: "#fff",
                                        borderRadius: "12px",
                                        padding: { xs: "14px", sm: "16px" },
                                        border: "1px solid rgba(15,23,42,0.06)",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.75,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: "50%",
                                                backgroundColor: `${colors.mainSecondary}1F`,
                                                color: colors.mainSecondary,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontFamily: "'Montserrat', sans-serif",
                                                fontWeight: 800,
                                                fontSize: "0.72rem",
                                            }}
                                        >
                                            {idx + 1}
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily: "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                fontSize: "0.88rem",
                                                color: colors.primary,
                                            }}
                                        >
                                            {step.title}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "0.8rem",
                                            color: "#6b7280",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {step.body}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>
        </CollectPageWrapper>
    );
};

export default CollectSetup;
